import React, { useRef } from 'react';
import type { UnfixableDomainRule } from '../../types/guideline';
import {
    parseUnfixableRulesExcel,
    UNFIXABLE_COL_ASP,
    UNFIXABLE_COL_DESCRIPTION,
    UNFIXABLE_COL_DOMAIN,
    UNFIXABLE_COL_MEDIA,
    UNFIXABLE_COL_OPERATION_COMPANY,
    UNFIXABLE_REQUIRED_HEADERS,
    UNFIXABLE_RULES_SHEET_NAME,
} from '../../utils/unfixableRulesExcel';
import { decodeDomainForDisplay } from '../../utils/urlDisplay';

export interface UnfixableRulesSectionProps {
    enableUnfixableClassification: boolean;
    onEnableUnfixableClassificationChange: (value: boolean) => void;
    unfixableByDomain: UnfixableDomainRule[];
    onUnfixableByDomainChange: (rules: UnfixableDomainRule[]) => void;
    unfixableUploadError: string | null;
    onUnfixableUploadErrorChange: (message: string | null) => void;
    pendingUnfixableFile: File | null;
    onPendingUnfixableFileChange: (file: File | null) => void;
    onDownload: () => void;
    downloadDisabled: boolean;
    onDirty: () => void;
}

const UnfixableRulesSection: React.FC<UnfixableRulesSectionProps> = ({
    enableUnfixableClassification,
    onEnableUnfixableClassificationChange,
    unfixableByDomain,
    onUnfixableByDomainChange,
    unfixableUploadError,
    onUnfixableUploadErrorChange,
    pendingUnfixableFile,
    onPendingUnfixableFileChange,
    onDownload,
    downloadDisabled,
    onDirty,
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        onUnfixableUploadErrorChange(null);
        try {
            const buf = await file.arrayBuffer();
            const parsed = parseUnfixableRulesExcel(buf);
            onUnfixableByDomainChange(
                parsed.map((p) => ({
                    id: p.id,
                    domain: p.domain,
                    description: p.description,
                    asp: p.asp,
                    operation_company: p.operation_company,
                    media_name: p.media_name,
                    source_row: p.sourceRow ?? null,
                }))
            );
            onPendingUnfixableFileChange(file);
            onDirty();
        } catch (err: any) {
            onUnfixableUploadErrorChange(err?.message || 'Excel の解析に失敗しました');
        }
    };

    return (
        <div className="category-section unfixable-rules-section">
            <div className="category-header">
                <h3 className="section-subtitle">修正不可リスト（ドメイン別）</h3>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => fileInputRef.current?.click()}
                    >
                        Excel をアップロード
                    </button>
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={onDownload}
                        disabled={downloadDisabled}
                    >
                        Excel をダウンロード
                    </button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                    />
                </div>
            </div>
            <p className="unfixable-domain-hint">
                シート名は「{UNFIXABLE_RULES_SHEET_NAME}」。列は{' '}
                {UNFIXABLE_REQUIRED_HEADERS.map((h, i) => (
                    <span key={h}>
                        {i > 0 ? '、' : ''}「{h}」
                    </span>
                ))}
                （{UNFIXABLE_COL_DOMAIN}・{UNFIXABLE_COL_DESCRIPTION} は行ごとに必須）。
                {UNFIXABLE_COL_DOMAIN} 列は URL（例: <code>https://www.example.com/path</code>）もドメインに変換して保存します。
                <code>www.</code> は除去し小文字に正規化します。日本語ドメイン（
                <code>xn--</code>）はプレビューで日本語表示します。重複がある場合は該当行をまとめて警告します。
            </p>
            <div className="search-toggle-section">
                <label className="search-toggle-label">
                    <input
                        type="checkbox"
                        checked={enableUnfixableClassification}
                        onChange={(e) => {
                            onEnableUnfixableClassificationChange(e.target.checked);
                            onDirty();
                        }}
                    />
                    <span>修正可否の自動分類を有効化</span>
                </label>
            </div>
            {unfixableUploadError && (
                <div className="error-message unfixable-upload-error" style={{ marginTop: '8px' }}>
                    {unfixableUploadError}
                </div>
            )}
            {pendingUnfixableFile && (
                <p className="unfixable-file-note">
                    アップロード済み（未保存）: {pendingUnfixableFile.name} — 保存すると反映されます
                </p>
            )}
            <p className="unfixable-count-note">{unfixableByDomain.length} ドメイン</p>
            <div className="unfixable-preview-table-wrap">
                <table className="unfixable-preview-table">
                    <thead>
                        <tr>
                            <th>{UNFIXABLE_COL_ASP}</th>
                            <th>{UNFIXABLE_COL_OPERATION_COMPANY}</th>
                            <th>{UNFIXABLE_COL_MEDIA}</th>
                            <th>{UNFIXABLE_COL_DOMAIN}</th>
                            <th>{UNFIXABLE_COL_DESCRIPTION}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {unfixableByDomain.length === 0 ? (
                            <tr>
                                <td colSpan={5} className="unfixable-empty-cell">
                                    リストが空です。Excel をアップロードするか、YAML で編集してください。
                                </td>
                            </tr>
                        ) : (
                            unfixableByDomain.map((row) => (
                                <tr key={row.id}>
                                    <td>{row.asp || '—'}</td>
                                    <td>{row.operation_company || '—'}</td>
                                    <td>{row.media_name || '—'}</td>
                                    <td className="unfixable-domain-cell">{decodeDomainForDisplay(row.domain)}</td>
                                    <td className="unfixable-description-cell">{row.description}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default UnfixableRulesSection;
